import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { SectionHeading } from "../SectionHeading";
import { Reveal } from "../Reveal";
import { SOLUTIONS, IMAGES } from "../../data/content";
import { useLang } from "../../i18n/LanguageContext";

const ART = [IMAGES.sphere, IMAGES.neural, IMAGES.cloud];

const Showcase = ({ item, index, showLearnMore }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const reversed = index % 2 === 1;

  return (
    <div ref={ref} data-testid={`solution-${index}`} className="grid items-center gap-8 lg:grid-cols-2 lg:gap-16">
      <Reveal className={reversed ? "lg:order-2" : ""}>
        <div className="group relative aspect-[4/3] overflow-hidden rounded-3xl border border-white/[0.07] bg-[#0A0E17]">
          <motion.img
            style={{ y: imgY }}
            src={ART[index % ART.length]}
            alt={t(item.name)}
            loading="lazy"
            className="absolute inset-0 h-[120%] w-full object-cover opacity-70 transition-all duration-700 group-hover:opacity-90 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#02060D] via-transparent to-transparent" />
          <div className="absolute left-6 top-6 rounded-full border border-white/10 bg-black/40 backdrop-blur px-3 py-1 font-mono text-[10px] tracking-widest text-white/60">
            {t(SOLUTIONS.labelTag)}
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.1} className={reversed ? "lg:order-1" : ""}>
        <div>
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#F59E0B]/80">{t(item.industry)}</span>
          <h3 className="mt-3 text-3xl md:text-4xl font-medium tracking-tight text-white leading-tight">{t(item.name)}</h3>
          <p className="mt-5 text-lg text-white/55 leading-relaxed max-w-md">{t(item.value)}</p>
          <ul className="mt-7 grid grid-cols-2 gap-x-6 gap-y-3 max-w-md">
            {item.features.map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                <Check className="h-3.5 w-3.5 text-[#60A5FA]" /> {t(f)}
              </li>
            ))}
          </ul>
          {showLearnMore && (
            <Link to="/contact" className="mt-8 inline-flex items-center gap-1.5 text-sm text-[#93c5fd] hover:text-white transition-colors group">
              {t({ en: "Discuss a similar solution", no: "Diskuter en lignende løsning" })}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          )}
        </div>
      </Reveal>
    </div>
  );
};

export const Solutions = ({ limit, hideHeading }) => {
  const { t } = useLang();
  const items = limit ? SOLUTIONS.items.slice(0, limit) : SOLUTIONS.items;
  return (
    <section id="solutions" data-testid="solutions-section" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        {!hideHeading && <SectionHeading overline={t(SOLUTIONS.overline)} title={t(SOLUTIONS.title)} />}
        {!hideHeading && <p className="mt-6 max-w-2xl text-lg text-white/55 leading-relaxed">{t(SOLUTIONS.sub)}</p>}
        <div className={`${hideHeading ? "" : "mt-20"} space-y-24 md:space-y-32`}>
          {items.map((item, i) => (
            <Showcase key={i} item={item} index={i} showLearnMore={!!hideHeading} />
          ))}
        </div>
        {limit && (
          <Reveal delay={0.1}>
            <div className="mt-16 flex justify-center">
              <Link
                to="/solutions"
                data-testid="solutions-view-all"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-white/10 hover:border-white/30"
              >
                {t({ en: "View all example solutions", no: "Se alle eksempelløsninger" })}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
